import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  Clock,
  VideoCamera,
  ArrowRight,
  UsersThree,
  HandsPraying,
  Leaf,
  Heart,
  HandHeart,
  Butterfly,
  Info,
} from "@phosphor-icons/react";
import { IMAGES } from "../lib/site";
import SectionHeading from "../components/SectionHeading";
import OnlineChapelSection from "../components/OnlineChapelSection";
import OnlineClassRegistrationDialog from "../components/OnlineClassRegistrationDialog";
import Seo from "../components/Seo";

const classes = [
  {
    icon: HandsPraying,
    tag: "Grief",
    title: "Grief Workshop: First Year After Loss",
    cadence: "4-Week Format",
    duration: "60–75 minute sessions",
    desc: "A gentle guided workshop for those in early bereavement — reflection prompts, scripture, and shared space for tender conversation.",
  },
  {
    icon: Heart,
    tag: "Encouragement",
    title: "Chaplain-Led Encouragement Sessions",
    cadence: "Live Gathering",
    duration: "45 minutes",
    desc: "A short, faith-rooted gathering for prayer, encouragement, and reflection with Chaplain Derrick.",
  },
  {
    icon: Leaf,
    tag: "Devotional",
    title: "Spiritual Growth in Hard Seasons",
    cadence: "6-Session Devotional",
    duration: "45 minutes",
    desc: "A soulful devotional journey through scripture, silence, and shared reflection for anyone walking a hard season.",
  },
  {
    icon: UsersThree,
    tag: "Caregivers",
    title: "Caregiver Refresh Webinar",
    cadence: "Live Webinar",
    duration: "60 minutes",
    desc: "Encouragement, practical rest strategies, and spiritual care for those loving someone through serious illness.",
  },
  {
    icon: HandsPraying,
    tag: "Self-Care",
    title: "Self-Care for the Weary Soul",
    cadence: "Self-Paced Workshop",
    duration: "Watch anytime",
    desc: "Compassionate reflections and gentle practices for those who feel spiritually and emotionally depleted.",
  },
];

const onlineSupportOfferings = [
  {
    icon: HandHeart,
    tag: "Hospice Family Support",
    title: "Hospice Family Support — Online",
    subtitle: "Support, education, and conversation for the hospice journey.",
    body:
      "A welcoming online gathering for patients, caregivers, and family members navigating serious illness, hospice care, changing roles, anticipatory grief, and end-of-life concerns. Participants can receive practical education, compassionate support, and space for meaningful conversation.",
    accent: "primary",
    testId: "offering-hospice",
    programKey: "hospice_family_support",
  },
  {
    icon: Butterfly,
    tag: "Bereavement Support",
    title: "Bereavement Support — Online",
    subtitle: "A supportive place for life after loss.",
    body:
      "A gentle online gathering for individuals navigating grief after the death of someone they love. Sessions provide opportunities for reflection, grief education, supportive conversation, and encouragement while honoring that every person's grief journey is different.",
    accent: "secondary",
    testId: "offering-bereavement",
    programKey: "bereavement_support",
  },
];

const OnlineSupportOfferings = () => {
  const [dialog, setDialog] = useState({ open: false, programKey: null, programLabel: null });
  const openRegistration = (offering) =>
    setDialog({ open: true, programKey: offering.programKey, programLabel: offering.title });
  const closeRegistration = () => setDialog((d) => ({ ...d, open: false }));

  return (
  <>
  <section
    className="py-20 sm:py-28 bg-softblue/40"
    data-testid="online-support-offerings"
  >
    <div className="container-x">
      <SectionHeading
        overline="Online Support — Registration Open"
        title="Two complementary online gatherings offering education, support, and meaningful connection."
        align="center"
        className="mb-14"
      />
      <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 max-w-6xl mx-auto">
        {onlineSupportOfferings.map(
          (offering) => {
            const { icon: Icon, tag, title, subtitle, body, accent, testId, programKey } = offering;
            const anchorId = programKey === "hospice_family_support"
              ? "hospice-family-support"
              : programKey === "bereavement_support"
              ? "bereavement-support"
              : undefined;
            return (
            <div
              key={title}
              id={anchorId}
              data-testid={testId}
              className="group scroll-mt-24 bg-white rounded-2xl p-8 sm:p-10 border border-ink-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_18px_50px_-10px_rgba(15,23,42,0.15)] transition-all duration-300 flex flex-col"
            >
              <div className="flex items-start justify-between gap-4">
                <span
                  className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl ${
                    accent === "secondary"
                      ? "bg-secondary/15 text-secondary"
                      : "bg-softblue text-primary"
                  }`}
                >
                  <Icon size={24} weight="duotone" />
                </span>
                <span
                  className={`text-[10px] uppercase tracking-[0.18em] rounded-full px-3 py-1 font-semibold ${
                    accent === "secondary"
                      ? "bg-secondary/10 text-secondary"
                      : "bg-primary/5 text-primary"
                  }`}
                >
                  {tag}
                </span>
              </div>

              <h3 className="mt-6 font-heading text-2xl sm:text-3xl font-medium text-ink-900 leading-snug">
                {title}
              </h3>
              <p className="mt-3 text-ink-700 italic leading-relaxed">
                {subtitle}
              </p>
              <p className="mt-4 text-ink-700 leading-relaxed flex-1">
                {body}
              </p>

              <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-ink-700">
                <span className="inline-flex items-center gap-2">
                  <VideoCamera size={16} weight="duotone" className="text-primary" />
                  Hosted online
                </span>
              </div>

              <button
                type="button"
                onClick={() => openRegistration(offering)}
                data-testid={`${testId}-cta`}
                className="mt-7 inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold bg-primary text-white hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
              >
                Register for Online Support
                <ArrowRight size={16} weight="bold" />
              </button>
              <p
                data-testid={`${testId}-cta-subtext`}
                className="mt-3 text-xs text-ink-500 leading-relaxed"
              >
                Registration is now open. Meeting dates and online access
                information will be provided to registered participants.
              </p>
            </div>
            );
          }
        )}
      </div>
    </div>
  </section>

      <OnlineClassRegistrationDialog
        open={dialog.open}
        onClose={closeRegistration}
        program={dialog.programKey}
        programLabel={dialog.programLabel}
      />
    </>
  );
};

const Classes = () => {
  return (
    <div data-testid="page-classes">
      <Seo
        title="Classes, Workshops & Online Chapel"
        description="Join the Online Chapel by Zoom and hospice, grief, and bereavement classes for spiritual encouragement and community with Chaplain Derrick Hall."
      />
      {/* HERO */}
      <section className="py-20 sm:py-28">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 reveal">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              Online Classes &amp; Support
            </span>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] text-ink-900">
              Learn, connect, and receive support wherever you are.
            </h1>
            <p className="mt-6 text-ink-700 text-lg leading-relaxed max-w-xl">
              Join chaplain-led online support gatherings, grief and caregiver
              education, spiritual encouragement, and practical workshops for
              individuals, families, and communities.
            </p>
          </div>
          <div className="lg:col-span-5 reveal">
            <img
              src={IMAGES.community}
              alt="Online learning together"
              className="rounded-2xl shadow-xl border border-ink-100"
            />
          </div>
        </div>
      </section>

      {/* SECTION 2 — ONLINE SUPPORT (Registration Open) */}
      <OnlineSupportOfferings />

      {/* SECTION 3 — CLASSES & WORKSHOPS */}
      <section
        id="classes-workshops"
        className="py-20 sm:py-28 scroll-mt-24"
        data-testid="classes-workshops-section"
      >
        <div className="container-x">
          <SectionHeading
            overline="Classes & Workshops"
            title="Explore grief, caregiving, spiritual growth, encouragement, and self-care programs offered by Begin Again Living Ministries."
            align="center"
            className="mb-14"
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {classes.map(({ icon: Icon, title, cadence, duration, desc, tag }) => (
            <div
              key={title}
              data-testid={`class-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="bg-white rounded-2xl p-8 border border-ink-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_18px_50px_-10px_rgba(15,23,42,0.15)] transition-all duration-300 flex flex-col"
            >
              <div className="flex items-start justify-between">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-softblue text-primary">
                  <Icon size={24} weight="duotone" />
                </span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-primary bg-primary/5 rounded-full px-3 py-1 font-semibold">
                  {tag}
                </span>
              </div>
              <h3 className="mt-6 font-heading text-2xl font-medium text-ink-900 leading-snug">
                {title}
              </h3>
              <p className="mt-3 text-ink-700 leading-relaxed flex-1">{desc}</p>

              <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-ink-500">
                <span className="inline-flex items-center gap-1.5">
                  <Info size={14} weight="duotone" className="text-primary" />
                  {cadence}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock size={14} weight="duotone" className="text-primary" />
                  {duration}
                </span>
              </div>

              <Link
                to="/request-support"
                data-testid={`class-cta-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="mt-7 inline-flex items-center gap-1.5 text-primary font-semibold hover:gap-2.5 transition-all"
              >
                Learn More / Request Information
                <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — ONLINE CHAPEL */}
      <OnlineChapelSection variant="classes" />

      {/* SECTION 5 — FOR ORGANIZATIONS */}
      <section className="py-20 sm:py-28 bg-softblue/40" data-testid="classes-for-organizations">
        <div className="container-x text-center max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
            <span className="w-8 h-px bg-secondary" />
            For Organizations
          </span>
          <h2 className="mt-6 font-heading text-3xl sm:text-5xl font-medium text-ink-900 leading-[1.1]">
            Bring a Program to Your Organization
          </h2>
          <p className="mt-6 text-ink-700 text-lg leading-relaxed">
            Board &amp; care homes, senior living communities, hospices,
            hospitals, churches, businesses, and community organizations can
            request presentations, workshops, trainings, support programs,
            and speaking engagements.
          </p>
          <Link
            to="/organization-bookings"
            data-testid="classes-cta-host-btn"
            className="mt-8 inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
          >
            Request a Date &amp; Program
            <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>

      {/* SUPPORT DISCLAIMER — bottom, understated */}
      <section className="pb-16 sm:pb-20 bg-white" data-testid="classes-support-disclaimer">
        <div className="container-x max-w-3xl mx-auto">
          <div className="rounded-2xl border border-ink-100 bg-white px-6 py-5">
            <div className="flex items-start gap-3">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-softblue text-primary shrink-0">
                <Info size={16} weight="duotone" />
              </span>
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold">
                  A Note About Our Online Support
                </div>
                <p className="mt-2 text-sm text-ink-600 leading-relaxed">
                  BALM&rsquo;s online gatherings provide spiritual
                  encouragement, education, community, and supportive
                  conversation. They are not psychotherapy, licensed
                  counseling, or emergency crisis services. If someone is
                  experiencing an immediate crisis, they should seek
                  appropriate professional or emergency support.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Classes;
